import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { query, publicQuery, mutate } from '../graphql/client';
import { GET_USER, GET_ME, UPDATE_PROFILE, FOLLOW_USER, UNFOLLOW_USER } from '../graphql/operations';
import { getCached, setCache, getUserCacheKey, invalidateCache } from '../utils/cache';
import type { User, UpdateProfileInput } from '../types/api';

interface UseUserOptions {
  initialData?: User | null;
  isAuthenticated?: boolean;
}

interface UseUserResult {
  user: User | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

export function useUser(username: string, options?: UseUserOptions): UseUserResult {
  // Priority: initialData > cache > null
  const cacheKey = getUserCacheKey(username);
  const cached = getCached<User>(cacheKey);
  const initialUser = options?.initialData || cached;
  const isAuthenticated = options?.isAuthenticated ?? false;

  const [user, setUser] = useState<User | null>(initialUser);
  // If we have initial data, start with loading false (show data immediately)
  const [loading, setLoading] = useState(!initialUser);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUser = useCallback(async (showLoading = false) => {
    if (!username) {
      setLoading(false);
      return;
    }

    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      // Use authenticated query when logged in to get isFollowing field
      // Use publicQuery (API_KEY) for unauthenticated users so profiles still work
      const queryFn = isAuthenticated ? query : publicQuery;
      const data = await queryFn<{ getUser: User | null }>(GET_USER, { username });
      setUser(data.getUser);
      // Cache the result
      if (data.getUser) {
        setCache(cacheKey, data.getUser);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user'));
    } finally {
      setLoading(false);
    }
  }, [username, cacheKey, isAuthenticated]);

  useEffect(() => {
    // Only fetch once per username
    if (!hasFetched) {
      setHasFetched(true);
      fetchUser(!initialUser); // Show loading only if no initial data
    }
  }, [hasFetched, fetchUser, initialUser]);

  // Reset hasFetched when username or auth state changes
  useEffect(() => {
    setHasFetched(false);
  }, [username, isAuthenticated]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return null;
      // Filter out undefined values to avoid overwriting existing data
      const filteredUpdates = Object.fromEntries(
        Object.entries(updates).filter(([, v]) => v !== undefined)
      );
      const updated = { ...prev, ...filteredUpdates };
      // Update cache
      setCache(cacheKey, updated);
      return updated;
    });
  }, [cacheKey]);

  const refetch = useCallback(() => fetchUser(false), [fetchUser]);

  return { user, loading, error, refetch, updateUser };
}

interface UseMeResult {
  user: User | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  updateProfile: (input: UpdateProfileInput) => Promise<User>;
}

export function useMe(): UseMeResult {
  const cacheKey = 'me';
  const cached = getCached<User>(cacheKey);

  const [user, setUser] = useState<User | null>(cached);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchMe = useCallback(async () => {
    setError(null);

    try {
      const data = await query<{ getMe: User | null }>(GET_ME);
      setUser(data.getMe);
      // Cache the result
      if (data.getMe) {
        setCache(cacheKey, data.getMe);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch once on mount, skip if we already have cached data
    if (!hasFetched) {
      setHasFetched(true);
      if (!cached) {
        setLoading(true);
      }
      fetchMe();
    }
  }, [hasFetched, cached, fetchMe]);

  const updateProfile = useCallback(async (input: UpdateProfileInput): Promise<User> => {
    const data = await mutate<{ updateProfile: User }>(UPDATE_PROFILE, { input });
    setUser(data.updateProfile);
    // Update cache
    setCache(cacheKey, data.updateProfile);
    return data.updateProfile;
  }, []);

  return { user, loading, error, refetch: fetchMe, updateProfile };
}

interface UseFollowResult {
  isFollowing: boolean;
  loading: boolean;
  error: Error | null;
  follow: () => Promise<boolean>;
  unfollow: () => Promise<boolean>;
  toggle: () => Promise<boolean>;
}

export function useFollow(userId: string, initialFollowing = false): UseFollowResult {
  const [isFollowing, setIsFollowing] = useState(initialFollowing);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use ref to prevent race conditions in toggle
  const isFollowingRef = useRef(isFollowing);
  useEffect(() => {
    isFollowingRef.current = isFollowing;
  }, [isFollowing]);

  const follow = useCallback(async (): Promise<boolean> => {
    if (loading) return false; // Prevent double-clicks
    setLoading(true);
    setError(null);
    try {
      await mutate(FOLLOW_USER, { userId });
      setIsFollowing(true);
      // Invalidate feed cache so new user's commits appear
      invalidateCache('feed:');
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to follow');
      setError(error);
      toast.error('Failed to follow user');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userId, loading]);

  const unfollow = useCallback(async (): Promise<boolean> => {
    if (loading) return false; // Prevent double-clicks
    setLoading(true);
    setError(null);
    try {
      await mutate(UNFOLLOW_USER, { userId });
      setIsFollowing(false);
      // Invalidate feed cache
      invalidateCache('feed:');
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to unfollow');
      setError(error);
      toast.error('Failed to unfollow user');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userId, loading]);

  const toggle = useCallback(async (): Promise<boolean> => {
    // Use ref to get current value, not stale closure
    if (isFollowingRef.current) {
      return unfollow();
    } else {
      return follow();
    }
  }, [follow, unfollow]);

  return { isFollowing, loading, error, follow, unfollow, toggle };
}
